import React, { useState } from 'react';
import { useMsal } from "@azure/msal-react";
import { useNavigate } from 'react-router-dom';
import MessageInput from "../components/conversation/MessageInput";
import StickyInputBar from "../components/conversation/StickyInputBar";
import { useTranslation } from '../hooks/useTranslation';
import { authFetch } from '../utils/auth';
import { rateLimitMessage } from '../utils/rateLimitMessage';

// 429 copy for POST /api/conversations (routes/conversations.js limiter
// 'conversations/create'); other limiters get the generic fallback.
const CREATE_RATE_LIMIT = {
  ownLimiter: 'conversations/create',
  lead: 'Du har startet mange samtaler på kort tid.',
};

const NewConversation = () => {
  // Keep useMsal hook for auth context without destructuring
  useMsal();
  const navigate = useNavigate();
  const [initialMessage, setInitialMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);
  // Inline, dismissable 429 notice above the input; cleared on the next try.
  const [notice, setNotice] = useState(null);
  const { t } = useTranslation();

  const suggestedQuestions = [
    t('newConversation.suggestedQuestions.q1'),
    t('newConversation.suggestedQuestions.q2'),
    t('newConversation.suggestedQuestions.q3'),
    t('newConversation.suggestedQuestions.q4'),
  ];

  const handleCreateConversation = async (message) => {
    if (submitting || !message.trim()) return;
    setSubmitting(true);
    setNotice(null);
    try {
      // Create the conversation using the API utility
      const response = await authFetch('/api/conversations', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          title: message,
          initialMessage: message,
        }),
      });

      if (response.ok) {
        const conversation = await response.json();
        navigate(`/conversations/${conversation._id}`, { state: { initialMessage: message } });
      } else {
        console.error('Failed to create conversation');
        setSubmitting(false);
      }
    } catch (error) {
      console.error("Error creating conversation:", error);
      // 429: say so; the typed message stays in the input for the retry.
      // Every other error keeps the existing silent-console behaviour.
      if (error?.status === 429) setNotice(rateLimitMessage(error, CREATE_RATE_LIMIT));
      setSubmitting(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    handleCreateConversation(initialMessage);
  };

  const handleSuggestedQuestionClick = (question) => {
    handleCreateConversation(question); // Create conversation immediately
  };

  return (
    // Same column geometry as ConversationView: fills the scroll container,
    // negative bottom margin cancels the shell's p-4/md:p-8 bottom padding so
    // the sticky input sits at the bottom of the pane (see ConversationView.js).
    <div className="bg-background min-h-full flex flex-col -mb-4 md:-mb-8">
      <div className="flex-1 flex flex-col items-center justify-center w-full p-6 max-w-4xl mx-auto">
        <h2 className="text-3xl mb-4">{t('newConversation.title')}</h2>
        <div className="mb-4 max-w-screen-lg text-center">
          {suggestedQuestions.map((question, index) => (
            <button
              key={index}
              onClick={() => handleSuggestedQuestionClick(question)}
              disabled={submitting}
              className="bg-transparent rounded-lg py-2 px-4 m-2 hover:bg-neutral-100 border border-border-light text-sm font-medium cursor-pointer text-text-primary transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {question}
            </button>
          ))}
        </div>
      </div>

      <StickyInputBar>
        {/* Same warning banner as ConversationView's send notice. */}
        {notice && (
          <div
            role="status"
            className="flex items-start justify-between gap-3 bg-warning-50 border border-warning-200 text-warning-700 px-4 py-3 rounded-lg mb-3 text-sm"
          >
            <span>{notice}</span>
            <button
              type="button"
              onClick={() => setNotice(null)}
              className="shrink-0 -m-3 p-3 text-warning-700 hover:text-warning-900 transition-colors"
              aria-label="Lukk"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 20 20"
                fill="currentColor"
                className="w-5 h-5"
                aria-hidden="true"
              >
                <path d="M6.28 5.22a.75.75 0 00-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 101.06 1.06L10 11.06l3.72 3.72a.75.75 0 101.06-1.06L11.06 10l3.72-3.72a.75.75 0 00-1.06-1.06L10 8.94 6.28 5.22z" />
              </svg>
            </button>
          </div>
        )}
        <MessageInput
          newMessage={initialMessage}
          setNewMessage={setInitialMessage}
          handleSendMessage={handleSubmit}
          disabled={submitting}
        />
      </StickyInputBar>
    </div>
  );
};

export default NewConversation;
